import { useQuery } from "@tanstack/react-query"
import * as React from "react"

import {
  DetectorProfileList,
  detectorProfileOptionsQuery,
  type DetectorProfileResolved,
} from "@/components/domain/detector-profile"

/**
 * The settings the review applied, as part of its scope statement.
 *
 * A client reads a clean report as a complete check, and what counts as a
 * difference — whether emphasis was compared, whether "1/3" against "⅓" is one
 * — is part of what "checked" meant. So the report says which way each was
 * set.
 *
 * Values only, never where they came from: an engagement profile or a
 * deployment default is our vocabulary, and the client needs what was applied,
 * not how we chose it. Renders nothing for a review that recorded no settings
 * (one that predates them, or one still running), and nothing if the labels
 * cannot be loaded — printing raw setting keys to a client would be worse than
 * leaving the line off.
 */
export function ReportSettings({
  profile,
}: {
  profile: DetectorProfileResolved | null
}): JSX.Element | null {
  const headingId = React.useId()
  const options = useQuery(detectorProfileOptionsQuery(profile !== null))
  if (profile === null || options.isError) return null

  return (
    <section aria-labelledby={headingId} className="mt-5 border-t border-border-soft pt-4">
      <h3 id={headingId} className="text-body-sm font-semibold text-text">
        How differences were judged
      </h3>
      <DetectorProfileList options={options.data} resolved={profile} className="mt-2" />
    </section>
  )
}
